import { useEffect, useState } from 'react';

/**
 * Custom hook to track media query matches
 *
 * @param query - The media query string to match (e.g., '(max-width: 768px)')
 * @returns Boolean indicating if the media query matches
 *
 * @example
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * ```
 */
export const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.matchMedia(query).matches;
        }
        return false;
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

        // Set initial value
        setMatches(mediaQuery.matches);

        // Listen for changes
        mediaQuery.addEventListener('change', handler);

        return () => mediaQuery.removeEventListener('change', handler);
    }, [query]);

    return matches;
};

/**
 * Hook for mobile breakpoint
 * @returns Boolean indicating if viewport is mobile-sized (< 768px)
 */
export const useIsMobile = (): boolean => {
    return useMediaQuery('(max-width: 767px)');
};
